import { useBaseInfo } from '@/context/BaseInfoContext';
import { UserFullInfo } from '@/models/user';
import { Flex, Skeleton } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import AppointmentList from '../AppointmentList';
import UserDomainCountAsync from '../User/UserDomainCountAsync';
import CardUserList from './CardUserList';
import CouponUserList from './CouponUserList';
import MoremeClientInfo from './MoremeClientInfo';
import PointLogList from './PointLogList';
import UserBalanceLogList from './UserBalanceLogList';
import UserOrderList from './UserOrderList';
import { FootprintList } from './FootprintList';

const useStyles = createStyles(({ css, cx, token, isDarkMode }) => {
  const header = cx(css`
    background: ${token.colorBgContainer};
    border-top: 1px dashed ${token.colorBorder};
    border-bottom: 1px dashed ${token.colorBorder};
    display: flex;
    text-align: center;
    padding: ${token.paddingXS}px;
  `);
  const item = cx(css`
    flex: 1;
    border-radius: 1em;
    border: 1px solid transparent;
    padding: 0.3em 0 0.4em;
    margin: 0 0.3em;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    h4 {
      font-size: 1.1em;
      margin-bottom: 0;
    }
    h5 {
      font-size: 1em;
      color: #999;
      margin-bottom: 0;
      font-weight: normal;
    }
    &.active {
      background: ${token.colorPrimaryActive};
      border-color: ${token.colorPrimaryBorder};
      h4 {
        color: #fff;
      }
      h5 {
        color: #eee;
      }
    }
  `);
  const content = cx(css`
    &:not(:empty) {
      padding: 20px 15px;
    }
    &:empty {
      &:after {
        content: '点击选项查看详情';
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 35px;
        font-size: 14px;
      }
    }
  `);
  const actions = cx(css`
    &:not(:empty) {
      margin: 15px -15px -20px;
      padding-bottom: 10px;
      padding-top: 10px;
      display: flex;
      justify-content: center;
      background: ${token.colorBgContainer};
      border-top: 2px solid ${token.colorPrimaryBorder};
    }
  `);
  return { header, item, content, actions };
});

export type TabTypes = 'coupon' | 'point' | 'consume' | 'balance' | 'card' | 'appointment' | 'analyzer';

export type Props = {
  info: UserFullInfo;
  onReload: () => void;
  activeKey: TabTypes;
  switchToTab: (key: TabTypes) => void;
  moremeClientName: string;
  version: number;
  loading: boolean;
};

export const UserExtraInfo: React.FC<Props> = ({
  info,
  onReload,
  activeKey,
  switchToTab,
  moremeClientName,
  version,
  loading,
}: Props) => {
  const [baseInfo] = useBaseInfo();
  const { styles, cx } = useStyles();
  const { rechargeConfig } = baseInfo.poiConfig;

  const initLoading = !info;
  if (initLoading) {
    return (
      <Flex vertical gap={10} style={{ padding: 10 }}>
        <Flex gap={10}>
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
        </Flex>
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </Flex>
    );
  }

  const { user } = info;
  return (
    <>
      <div className={styles.header}>
        <div
          className={cx(styles.item, { active: activeKey === 'coupon' })}
          onClick={() => {
            switchToTab('coupon');
          }}>
          <h4>{info.couponUserList.length}</h4>
          <h5>优惠券</h5>
        </div>
        <div
          className={cx(styles.item, { active: activeKey === 'point' })}
          onClick={() => {
            switchToTab('point');
          }}>
          <h4>{user.point}</h4>
          <h5>积分</h5>
        </div>
        <div
          className={cx(styles.item, { active: activeKey === 'consume' })}
          onClick={() => {
            switchToTab('consume');
          }}>
          <h4>
            &yen;
            <UserDomainCountAsync version={version} type={'consume'} userId={user.id} fallback={'-'} />
          </h4>
          <h5>订单</h5>
        </div>

        <div
          className={cx(styles.item, { active: activeKey === 'appointment' })}
          onClick={() => {
            switchToTab('appointment');
          }}>
          <h4>预约</h4>
        </div>

        {rechargeConfig.enabled ? (
          <div
            className={cx(styles.item, { active: activeKey === 'balance' })}
            onClick={() => {
              switchToTab('balance');
            }}>
            {user.balance === undefined ? <h4>未充值</h4> : <h4>&yen;{user.balance}</h4>}
            <h5>余额</h5>
          </div>
        ) : null}

        <div
          className={cx(styles.item, { active: activeKey === 'card' })}
          onClick={() => {
            switchToTab('card');
          }}>
          <h4>
            <UserDomainCountAsync version={version} type={'card'} userId={user.id} fallback={'-'} />
          </h4>
          <h5>预付卡</h5>
        </div>

        <div
          className={cx(styles.item, { active: activeKey === 'analyzer' })}
          onClick={() => {
            switchToTab('analyzer');
          }}>
          <h4>检测仪</h4>
          <h5>{moremeClientName || 'N/A'}</h5>
        </div>
      </div>

      <div className={styles.content}>
        {activeKey === 'card' && <CardUserList userId={user.id} />}
        {activeKey === 'consume' && <UserOrderList userId={user.id} />}
        {activeKey === 'appointment' && (
          <AppointmentList
            miniMode
            defaultQuery={{
              userId: String(user.id),
              pageSize: 3,
            }}
            updateQuery={() => {}}
          />
        )}
        {activeKey === 'point' && (
          <PointLogList user={user} actionAreaClassName={styles.actions} onUserPointChange={onReload} />
        )}
        {activeKey === 'balance' && (
          <UserBalanceLogList userId={user.id} actionAreaClassName={styles.actions} onBalanceChange={onReload} />
        )}
        {activeKey === 'coupon' && (
          <CouponUserList
            user={user}
            couponUserList={info.couponUserList}
            actionAreaClassName={styles.actions}
            onCouponUserChange={onReload}
          />
        )}
        {activeKey === 'analyzer' && (
          <MoremeClientInfo user={user} onDataChange={onReload} actionAreaClassName={styles.actions} />
        )}
      </div>
    </>
  );
};
